import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Building2, User } from 'lucide-react';
import { useTranslation } from '../i18n/I18nContext';
import QoldoLogo from './QoldoLogo';
import LanguageSwitcher from './LanguageSwitcher';
import { useViewportHeight, useLockBodyScroll } from '../hooks/useViewportHeight';

export default function StartScreen({ onStart, courseName, clientStores, experienceType }) {
  const { t, tm } = useTranslation();
  
  // iOS Safari viewport height fix
  useViewportHeight();
  useLockBodyScroll(true);
  
  // Use client stores if provided, otherwise fallback to empty array
  // Sort stores alphabetically by name for consistent display
  const stores = (clientStores || []).slice().sort((a, b) => 
    a.name.localeCompare(b.name, 'nl', { sensitivity: 'base' })
  );
  const hasStores = stores.length > 0;
  
  // State now uses store_id instead of free text
  const [storeId, setStoreId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errors, setErrors] = useState({});

  // Helper to find store by id
  const getStoreById = (id) => stores.find(s => s.id === id);

  // Pre-fill from localStorage on mount (but don't auto-submit)
  useEffect(() => {
    const storedStoreId = localStorage.getItem('qoldo_store_id');
    const storedFirstName = localStorage.getItem('qoldo_first_name');
    const storedLastName = localStorage.getItem('qoldo_last_name');
    // Backwards compatibility: try old user_name if first/last not set
    const storedUserName = localStorage.getItem('qoldo_user_name');
    
    // Validate that stored store_id still exists in client's store list
    if (storedStoreId) {
      const storeExists = stores.find(s => s.id === storedStoreId);
      if (storeExists) {
        setStoreId(storedStoreId);
      }
    }
    if (storedFirstName) setFirstName(storedFirstName);
    if (storedLastName) setLastName(storedLastName);
    // Backwards compat: if old user_name exists but no first/last name, use it as first name
    if (!storedFirstName && !storedLastName && storedUserName) {
      setFirstName(storedUserName);
    }
  }, [stores.length]); // Only re-run when stores array changes

  const handleStart = () => {
    const newErrors = {};
    
    // Only require store if stores are available
    if (hasStores && !storeId) {
      newErrors.store = t('player_start_store_error');
    }
    
    if (!firstName.trim()) {
      newErrors.firstName = t('player_start_first_name_error');
    }
    
    if (!lastName.trim()) {
      newErrors.lastName = t('player_start_last_name_error');
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Get the store object for the selected id (may be null if no stores)
    const selectedStore = storeId ? getStoreById(storeId) : null;
    
    // Save to localStorage for next time
    if (storeId && selectedStore) {
      localStorage.setItem('qoldo_store_id', storeId);
      localStorage.setItem('qoldo_store_name', selectedStore.name);
    }
    localStorage.setItem('qoldo_first_name', firstName.trim());
    localStorage.setItem('qoldo_last_name', lastName.trim());
    // Also save combined name for backwards compatibility
    const fullName = `${firstName.trim()} ${lastName.trim()}`;
    localStorage.setItem('qoldo_user_name', fullName);
    
    // Call parent handler with both store_id and store_name
    onStart({
      store_id: storeId || '',
      store_name: selectedStore?.name || '',
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      user_name: fullName  // Combined name for backwards compatibility
    });
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleStart();
    }
  };

  return (
    <div className="player-start-root bg-gray-50">
      {/* Content area - centered, no scroll */}
      <div className="player-start-content">
        <Card className="player-start-card shadow-lg">
          <CardHeader className="text-center space-y-2 pb-3 pt-4">
            {/* Qoldo Logo */}
            <QoldoLogo className="mb-1" />
            
            <div>
              <h2 className="text-base font-semibold">
                {t('player_start_welcome', { courseName: courseName || t('player_start_title') })}
              </h2>
              <p className="text-gray-600 mt-1 text-xs">
                {t('player_start_intro')}
              </p>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-3 pt-0 pb-4 px-4">
            {/* Store Selection Dropdown - only show if client has stores */}
            {hasStores && (
              <div className="space-y-1">
                <Label htmlFor="store-select" className="flex items-center gap-1.5 text-xs">
                  <Building2 className="h-3.5 w-3.5" />
                  {tm('player_start_store_label', experienceType)}
                </Label>
                <Select
                  value={storeId}
                  onValueChange={(value) => {
                    setStoreId(value);
                    setErrors(prev => ({ ...prev, store: null }));
                  }}
                >
                  <SelectTrigger 
                    id="store-select"
                    className={`h-9 text-sm ${errors.store ? 'border-red-500' : ''}`}
                  >
                    <SelectValue placeholder={t('player_start_store_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((store) => (
                      <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.store && (
                <p className="text-xs text-red-500">{errors.store}</p>
              )}
            </div>
            )}
            
            {/* First Name */}
            <div className="space-y-1">
              <Label htmlFor="first-name" className="flex items-center gap-1.5 text-xs">
                <User className="h-3.5 w-3.5" />
                {t('player_start_first_name_label')}
              </Label>
              <Input
                id="first-name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setErrors(prev => ({ ...prev, firstName: null }));
                }}
                onKeyPress={handleKeyPress}
                placeholder={t('player_start_first_name_placeholder')}
                className={`h-9 text-sm ${errors.firstName ? 'border-red-500' : ''}`}
              />
              {errors.firstName && (
                <p className="text-xs text-red-500">{errors.firstName}</p>
              )}
            </div>
            
            {/* Last Name */}
            <div className="space-y-1">
              <Label htmlFor="last-name" className="flex items-center gap-1.5 text-xs">
                <User className="h-3.5 w-3.5" />
                {t('player_start_last_name_label')}
              </Label>
              <Input
                id="last-name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  setErrors(prev => ({ ...prev, lastName: null }));
                }}
                onKeyPress={handleKeyPress}
                placeholder={t('player_start_last_name_placeholder')}
                className={`h-9 text-sm ${errors.lastName ? 'border-red-500' : ''}`}
              />
              {errors.lastName && (
                <p className="text-xs text-red-500">{errors.lastName}</p>
              )}
            </div>
            
            {/* Info - compact */}
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
              <p>{t('player_start_info')}</p>
            </div>
            
            {/* Start Button */}
            <Button 
              onClick={handleStart}
              className="w-full h-10"
            >
              {tm('player_start_button', experienceType)}
            </Button>
            
            {/* Language switcher */}
            <div className="flex justify-center pt-1">
              <LanguageSwitcher />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
